// ============================================================
// DESIGN PREVIEW MODE
// ============================================================
// Temporary development utility — bypasses route guards so all
// portal sections are browsable before real auth is wired up.
//
// TO DISABLE:
//   Set DESIGN_PREVIEW_MODE = false
//   All route guards and auth checks will immediately re-activate.
// ============================================================

export const DESIGN_PREVIEW_MODE = false;

// ------------------------------------------------------------
// Preview roles available in the switcher
// ------------------------------------------------------------
export type PreviewRole =
  | "client"
  | "parent"
  | "clinician"
  | "supervisor"
  | "staff"
  | "admin"
  | "marketing"
  | "superadmin";

export interface PreviewRoleConfig {
  role: PreviewRole;
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
  color: string;
  /** Sections visible to this role */
  sections: string[];
  /** What this role cannot access */
  restrictions: string[];
}

export const PREVIEW_ROLES: PreviewRoleConfig[] = [
  {
    role: "client",
    label: "Client",
    shortLabel: "Client",
    description: "Client portal only — dashboard, appointments, messages, billing, documents",
    icon: "ri-user-line",
    color: "bg-teal-50 border-teal-200 text-teal-700",
    sections: ["Client Portal"],
    restrictions: ["No access to clinical, admin, or marketing areas"],
  },
  {
    role: "parent",
    label: "Parent / Guardian",
    shortLabel: "Parent",
    description: "Parent portal — child profiles, appointments, billing, forms, messages, no clinical documentation",
    icon: "ri-parent-line",
    color: "bg-emerald-50 border-emerald-200 text-emerald-700",
    sections: ["Client Portal"],
    restrictions: ["No access to clinical charts", "No admin/staff routes", "No child clinical documentation"],
  },
  {
    role: "clinician",
    label: "Clinician",
    shortLabel: "Clinician",
    description: "Clinical charts, documentation, treatment plans, measures, resources — no billing amounts",
    icon: "ri-stethoscope-line",
    color: "bg-sky-50 border-sky-200 text-sky-700",
    sections: ["Staff Portal"],
    restrictions: ["No billing amounts", "No admin operations", "No marketing access"],
  },
  {
    role: "staff",
    label: "Staff",
    shortLabel: "Staff",
    description: "Assigned operational work and explicitly delegated capabilities",
    icon: "ri-team-line",
    color: "bg-slate-50 border-slate-200 text-slate-700",
    sections: ["Staff Portal"],
    restrictions: ["No implicit clinical chart access", "No implicit Billing or administration access"],
  },
  {
    role: "supervisor",
    label: "Supervisor",
    shortLabel: "Supervisor",
    description: "Own clinical caseload (as clinician) + supervisee review tools, signature reminders, documentation alerts",
    icon: "ri-shield-check-line",
    color: "bg-indigo-50 border-indigo-200 text-indigo-700",
    sections: ["Staff Portal", "Supervision"],
    restrictions: ["No billing amounts", "No admin operations", "No marketing access"],
  },
  {
    role: "admin",
    label: "Admin",
    shortLabel: "Admin",
    description: "Booking admin, appointments, billing follow-up, intake tracking, availability, document status, admin messages",
    icon: "ri-settings-3-line",
    color: "bg-amber-50 border-amber-200 text-amber-700",
    sections: ["Admin Portal"],
    restrictions: ["No psychotherapy notes", "No treatment plans", "No clinical measures or diagnoses", "No clinical chart content unless separately granted"],
  },
  {
    role: "marketing",
    label: "Marketing",
    shortLabel: "Marketing",
    description: "Subscriber overview, campaign workspace, funnel performance, GHL sync — no clinical or billing access",
    icon: "ri-megaphone-line",
    color: "bg-rose-50 border-rose-200 text-rose-700",
    sections: ["Marketing Portal"],
    restrictions: ["No clinical charts, notes, or documents", "No client messages", "No billing amounts", "No admin operations"],
  },
  {
    role: "superadmin",
    label: "SuperAdmin",
    shortLabel: "SuperAdmin",
    description: "Full access to all portal areas — Admin, Staff, Supervision, Marketing, Role Management",
    icon: "ri-key-2-line",
    color: "bg-violet-50 border-violet-200 text-violet-700",
    sections: ["Admin Portal", "Staff Portal", "Supervision", "Marketing Portal", "Role Management"],
    restrictions: [],
  },
];

// ------------------------------------------------------------
// Preview role persistence (sessionStorage — clears on close)
// ------------------------------------------------------------
const PREVIEW_ROLE_KEY = "design_preview_role";

const VALID_PREVIEW_ROLES: PreviewRole[] = ["client", "parent", "clinician", "supervisor", "staff", "admin", "marketing", "superadmin"];

export function getPreviewRole(): PreviewRole {
  if (!DESIGN_PREVIEW_MODE) return "client";
  const stored = sessionStorage.getItem(PREVIEW_ROLE_KEY);
  if (stored && VALID_PREVIEW_ROLES.includes(stored as PreviewRole)) {
    return stored as PreviewRole;
  }
  // Default to SuperAdmin so the portal is fully navigable in preview
  return "superadmin";
}

export function setPreviewRole(role: PreviewRole): void {
  sessionStorage.setItem(PREVIEW_ROLE_KEY, role);
  window.dispatchEvent(new Event("previewRoleChanged"));
}

// ------------------------------------------------------------
// Permission helpers in preview mode
// These mirror the real auth helpers but use the selected mock role
// SuperAdmin passes ALL guards automatically
// ------------------------------------------------------------

export function previewHasAdminAccess(): boolean {
  const r = getPreviewRole();
  return r === "admin" || r === "superadmin";
}

export function previewHasClinicalAccess(): boolean {
  const r = getPreviewRole();
  // Supervisor has clinical access (they are clinicians + more)
  return r === "clinician" || r === "supervisor" || r === "superadmin";
}

export function previewHasMarketingAccess(): boolean {
  const r = getPreviewRole();
  return r === "marketing" || r === "superadmin";
}

export function previewIsSuperAdmin(): boolean {
  return getPreviewRole() === "superadmin";
}

export function previewCanAccessSupervisor(): boolean {
  const r = getPreviewRole();
  return r === "supervisor" || r === "superadmin";
}

export function previewIsClient(): boolean {
  const r = getPreviewRole();
  return r === "client" || r === "parent";
}

/**
 * Can this preview role see billing dollar amounts?
 * Clinicians/Staff cannot. Everyone else on appropriate pages can.
 */
export function previewCanSeeBillingAmounts(): boolean {
  const r = getPreviewRole();
  return r === "admin" || r === "superadmin" || r === "client" || r === "parent";
}

/**
 * Can this preview role see clinical chart content?
 */
export function previewCanSeeClinicalContent(): boolean {
  const r = getPreviewRole();
  return r === "clinician" || r === "supervisor" || r === "superadmin";
}

/**
 * Can this preview role see supervision tools?
 */
export function previewCanSeeSupervisorTools(): boolean {
  const r = getPreviewRole();
  return r === "supervisor" || r === "superadmin";
}

// ------------------------------------------------------------
// Nav sections visible to each role
// SuperAdmin sees ALL sections
// ------------------------------------------------------------

export function previewGetVisibleSections(): {
  showClient: boolean;
  showAdmin: boolean;
  showStaff: boolean;
  showSupervision: boolean;
  showMarketing: boolean;
  showRoleAdmin: boolean;
} {
  const r = getPreviewRole();
  const isSuperAdmin = r === "superadmin";
  const isParent = r === "parent";
  return {
    showClient: r === "client" || isParent,
    // SuperAdmin sees admin nav + Practice + Roles only — staff/supervision/marketing
    // nav sets appear when the corresponding role is explicitly selected in the switcher
    showAdmin: r === "admin" || isSuperAdmin,
    showStaff: r === "clinician" || r === "supervisor" || r === "staff",
    showSupervision: r === "supervisor",
    showMarketing: r === "marketing",
    showRoleAdmin: isSuperAdmin,
  };
}

// ------------------------------------------------------------
// Landing route helper — where should this preview role land?
// ------------------------------------------------------------
export function getPreviewDefaultRoute(): string {
  const r = getPreviewRole();
  switch (r) {
    case "admin":      return "/admin";
    case "superadmin":      return "/admin";
    case "clinician":  return "/staff";
    case "supervisor": return "/staff";
    case "staff":      return "/staff/tasks";
    case "marketing":  return "/marketing";
    case "parent":     return "/clientportal/dashboard";
    case "client":
    default:           return "/clientportal/dashboard";
  }
}
